import { useMemo } from 'react';
import type { ContestSource } from '@betterpool/api-types/contests-service';
import { ContestStatus } from '@betterpool/api-types/contests-service';
import { useMantineTheme } from '@mantine/core';

import dt from '~/testing';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';

import { getIsEOContest } from '../../utils/getIsEOContest';

import CardContainer from './CardContainer';
import useStyles from './ContestCard.styles';
import { ContestCardActions } from './ContestCardActions';
import ContestCardBanner from './ContestCardBanner';
import ContestCardHeader from './ContestCardHeader';
import ContestCardProgressBar from './ContestCardProgressBar';
import ContestCardFooter from './Footer/ContestCardFooter';
import type { ContestCardProps } from './types';

function ContestCard({
  actionBanner,
  contest,
  currentSlateStatus,
  isBadgeIconSectionEnabled = true,
  isBannerEnabled = false,
  isCardMenuVisible = true,
  isContestThumbnailShown,
  isCountdownPillEnabled = false,
  isInvitesManagerEnabled = false,
  isMassCommunicationEnabled = false,
  isMyContestsView = false,
  isNotAllowedInLocation,
  isPlayWithPeopleYouFollowEnabled = false,
  isEntrantManagerEnabled = false,
  isManageContestEnabled = false,
  isShareContestEnabled = false,
  isProgressEnabled = true,
  isStatic,
  isSurvivor = false,
  leaderboard,
  onClick,
  promoIndex,
  promoSource,
  survivor,
}: ContestCardProps) {
  const theme = useMantineTheme();

  const { classes } = useStyles({ isStatic });
  const contestCardBg = (() => {
    if (isNotAllowedInLocation) {
      return theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.gray[7];
    }

    return undefined;
  })();

  // v2-parsed contests carry entitySource, not source.
  const contestSource = (contest as { entitySource?: string }).entitySource ?? contest.source;
  const badgeName = useMemo(() => {
    if (!contestSource) return null;
    const contestSourceSanitized = contestSource.toUpperCase() as ContestSource;
    // Hide EO badges on homepage promotions
    if (promoSource && getIsEOContest(contestSourceSanitized)) return null;
    return getIsEOContest(contestSourceSanitized) ? contestSourceSanitized : null;
  }, [contestSource, promoSource]);

  const isEntertainmentOnly =
    (contest as Contest).settings?.entertainmentOnlySettings?.enabled ||
    (contest as MyUserContest).isEntertainmentOnly;

  const isCommissioner = useMemo(
    () => isManageContestEnabled || isInvitesManagerEnabled || isEntrantManagerEnabled,
    [isManageContestEnabled, isInvitesManagerEnabled, isEntrantManagerEnabled]
  );

  const shouldShowContestCardActions =
    isCommissioner ||
    isMassCommunicationEnabled ||
    isPlayWithPeopleYouFollowEnabled ||
    isShareContestEnabled ||
    !!actionBanner;

  return (
    <CardContainer
      p="md"
      shadow={isStatic ? '0 8px 40px 0 rgba(0, 0, 0, 0.1)' : 'sm'}
      bg={contestCardBg}
      radius="lg"
      className={classes.container}
      data-test-id={dt.common.components.contestCard}
      contest={contest}
      onClick={onClick}
      isStatic={isStatic}
      promoSource={promoSource}
      promoIndex={promoIndex}
      currentSlateStatus={currentSlateStatus}
      shouldBlockForServiceFee={isMyContestsView}
    >
      <ContestCardBanner
        contest={contest}
        isBannerEnabled={isBannerEnabled}
        isCountdownPillEnabled={isCountdownPillEnabled}
        bleedPadding={theme.spacing.md}
      />
      <ContestCardHeader
        contest={contest}
        isBadgeIconSectionEnabled={isBadgeIconSectionEnabled}
        isCardMenuVisible={isCardMenuVisible}
        isContestThumbnailShown={isContestThumbnailShown}
        isCommissioner={isCommissioner}
        badgeName={badgeName}
      />
      {isProgressEnabled && (
        <ContestCardProgressBar
          leaderboard={leaderboard}
          contest={contest}
          isSurvivor={isSurvivor}
          isCommissioner={isCommissioner}
        />
      )}
      <ContestCardFooter
        winning={leaderboard?.userPayout}
        userSegmentedPayouts={leaderboard?.userSegmentedPayouts}
        entryPrice={contest.entry_fee}
        isSurvivor={isSurvivor}
        contest={contest}
        userAliveEntries={survivor?.userAliveEntries ?? 0}
        actionBanner={actionBanner}
        isSettled={contest.status === ContestStatus.FINALIZED}
        isMyContestsView={isMyContestsView}
        totalEntries={leaderboard?.totalSpots}
        winningEntries={leaderboard?.payoutSpots}
        rank={leaderboard?.positions[0]?.rank}
        isEntertainmentOnly={isEntertainmentOnly}
        survivor={survivor}
        isCommissioner={isCommissioner}
      />
      {shouldShowContestCardActions && (
        <ContestCardActions
          contest={contest}
          isMassCommunicationEnabled={isMassCommunicationEnabled}
          isPlayWithPeopleYouFollowEnabled={isPlayWithPeopleYouFollowEnabled}
          isEntertainmentOnly={isEntertainmentOnly}
          isManageContestEnabled={isManageContestEnabled}
          isShareContestEnabled={isShareContestEnabled}
          actionBanner={actionBanner}
          isCommissioner={isCommissioner}
        />
      )}
    </CardContainer>
  );
}

export default ContestCard;
